// Import necessary dependencies for React hooks, API, and components
import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api'; // Custom API module for making HTTP requests
import VehicleChecklistAddModal from './modals/VchicleChecklistAddModal/VechicleCheclistAddmodal'; // Fixed import path
import Pagination from '../Tools/Pagination'; // Component for pagination controls

// VehiclechecklistTab component for managing vehicle checklist items
const VehiclechecklistTab = () => {
  // State management for checklists data and UI
  const [checklists, setChecklists] = useState([]); // Stores the list of checklists
  const [isLoading, setIsLoading] = useState(true); // Loading state indicator
  const [showModal, setShowModal] = useState(false); // Controls modal visibility
  const [formData, setFormData] = useState({ // Form data state for add/edit
    name: '', // Checklist item name
    criteria: '', // Checklist item criteria
    child: [] // Array of sub-items (child checklist items)
  });
  const [isEditMode, setIsEditMode] = useState(false); // Tracks if we're in edit mode

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1); // Current page number
  const [itemsPerPage] = useState(10); // Number of items to display per page

  // Calculate pagination values
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = checklists.slice(indexOfFirstItem, indexOfLastItem);

  // Fetch checklists when component mounts
  useEffect(() => {
    fetchChecklists();
  }, []);

  /**
   * Fetches checklists from the API
   * Handles both success and error cases with appropriate user feedback
   */
  const fetchChecklists = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/ems/vehicle-checklist');
      
      // API returns { status: "success", data: [...] } → extract the array
      const apiData = response.data?.data || response.data || [];
      const dataArray = Array.isArray(apiData) ? apiData : [];
      
      setChecklists(dataArray);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching checklists:', error);
      setChecklists([]);
      setIsLoading(false);
      alert('Failed to fetch checklists. Please try again later.');
    }
  };

  /**
   * Handles input changes in the form
   * @param {Object} e - The event object
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Handles saving or updating a checklist item
   */
  const handleSave = async () => {
    try {
      if (isEditMode) {
        await axiosInstance.put(`/ems/vehicle-checklist/${formData.id}`, formData);
        alert('Checklist item updated successfully!');
      } else {
        await axiosInstance.post('/ems/vehicle-checklist', formData);
        alert('Checklist item added successfully!');
      }
      fetchChecklists();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving checklist:', error);
      alert(`Failed to ${isEditMode ? 'update' : 'add'} checklist. Please try again.`);
    }
  };

  /**
   * Opens the modal for adding a new checklist item
   */
  const openAddModal = () => {
    setFormData({
      name: '',
      criteria: '',
      child: []
    });
    setIsEditMode(false);
    setShowModal(true);
  };

  /**
   * Opens the modal for editing an existing checklist item
   * @param {Object} item - The checklist item to edit
   */
  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      name: item.name || '',
      criteria: item.criteria || '',
      child: Array.isArray(item.ems_child_vehicle_checklist) ? item.ems_child_vehicle_checklist : []
    });
    setIsEditMode(true);
    setShowModal(true);
  };

  /**
   * Handles deletion of a checklist item
   * @param {number} id - The ID of the checklist item to delete
   */
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this item from your checklist?');

    if (confirmDelete) {
      try {
        await axiosInstance.delete(`/ems/vehicle-checklist/${id}`);
        alert('Checklist item deleted successfully!');
        fetchChecklists();
      } catch (error) {
        console.error('Error deleting checklist:', error);
        alert('Failed to delete checklist item. Please try again.');
      }
    }
  };

  /**
   * Handles page change for pagination
   * @param {number} pageNumber - The page number to navigate to
   */
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render the component
  return (
    <div className="tab-pane active" id="specialities" role="tabpanel">
      {/* Filter Section */}
      <div className="filter-search mt-5">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form>
            <div className="form-group col-lg-4 col-md-4 col-12 p-2">
              <input type="text" className="form-control" placeholder="Item Name" />
            </div>

            <div className="form-group col-lg-4 col-md-4 col-12 p-2">
              <input type="submit" className="btn btn-outline-primary btn-rounded" value="Clear Filter" />
            </div>
          </form>
          {/* Add Checklist Button */}
          <button
            className="btn d-flex align-items-center justify-content-center btn-outline-primary"
            type="button"
            onClick={openAddModal}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Checklist
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="text-center py-4">Loading Checklist...</div>
      ) : (
        <>
          {/* Checklist Table */}
          <div className="py-3 px-0">
            <div className="table-responsive rounded-2 ros-table">
              <table className="table text-nowrap customize-table mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th>ID</th>
                    <th>Item</th>
                    <th>Criteria</th>
                    <th>Sub Items</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => {
                      const childList = Array.isArray(item.ems_child_vehicle_checklist)
                        ? item.ems_child_vehicle_checklist
                        : [];

                      return (
                        <tr key={item.id}>
                          {/* <td>{(currentPage - 1) * itemsPerPage + index + 1}</td> */}
                          <td>{item.id || '-'}</td>
                          <td>{item.name || '-'}</td>
                          <td>{item.criteria ?? '-'}</td>
                          <td>
                            {childList.length > 0
                              ? childList.map(sub => sub.name).filter(Boolean).join(', ')
                              : 'N/A'}
                          </td>
                          <td>
                            {/* Edit Button */}
                            <button
                              onClick={() => handleEdit(item)}
                              className="h4 me-3 border-0 bg-transparent"
                              title="Edit"
                            >
                              <i className="ti ti-edit text-warning"></i>
                            </button>
                            {/* Delete Button */}
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="h4 border-0 bg-transparent"
                              title="Delete"
                            >
                              <i className="ti ti-trash text-danger"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">No Checklists Found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {checklists.length > 0 && (
            <Pagination
              total={checklists.length}
              limit={itemsPerPage}
              page={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <VehicleChecklistAddModal
          show={showModal}
          onClose={() => setShowModal(false)}
          formData={formData}
          onChange={handleInputChange}
          onSave={handleSave}
          isEditMode={isEditMode}
        />
      )}
    </div>
  );
};

export default VehiclechecklistTab;